import {
  Button,
  createStyles,
  makeStyles,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core'
import { useCallback, useMemo, useState } from 'react'
import {
  DEFAULT_ROLE_HIERARCHY,
  RoleHierarchy,
} from '../../../shared/src/data/roleHierarchy/roleHierarchy'
import { useRoleHierarchyManager } from './queries/roleHierarchyManagement'
import { RoleHierarchySelector } from './roleHierarchySelector'
import { UserRoleSelector } from './userRoleSelector'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  }),
)

export function UserCreator({
  roleHierarchies,
}: {
  roleHierarchies: RoleHierarchy[]
}): JSX.Element {
  const {
    createUserMutation: { mutateAsync: createUser },
  } = useRoleHierarchyManager()
  const classes = useStyles()
  const [resultMessage, setResultMessage] = useState<{
    message: string
    type: 'error' | 'success'
  } | null>(null)
  const [email, setEmail] = useState<string>()
  const [name, setName] = useState<string>()
  const [position, setPosition] = useState<string>()
  const [password, setPassword] = useState<string>()
  const [roleHierarchy, setRoleHierarchy] = useState<RoleHierarchy>(
    DEFAULT_ROLE_HIERARCHY,
  )

  const isSubmitDisabled = useMemo(() => {
    return (
      !email ||
      !name ||
      !position ||
      !password ||
      !roleHierarchy?.branch?.name ||
      !roleHierarchy?.branch?.id ||
      !roleHierarchy?.organization?.name ||
      !roleHierarchy?.organization?.id ||
      !roleHierarchy?.role
    )
  }, [email, name, position, password, roleHierarchy])

  const handleUpdateButtonClicked = useCallback(async () => {
    setResultMessage(null)
    try {
      await createUser({
        position: position!,
        roleHierarchy: roleHierarchy!,
        email: email!,
        password: password!,
        name: name!,
      })
      setResultMessage({
        message: 'Updated user role hierarchy successfully.',
        type: 'success',
      })
    } catch (error) {
      setResultMessage({
        message: 'Failed updating user role hierarchy.',
        type: 'error',
      })
    }
  }, [createUser, position, roleHierarchy, email, password, name])

  return (
    <div className={classes.root}>
      <TextField
        type='email'
        label='Email'
        color='secondary'
        required
        value={email}
        onChange={(v) => setEmail(v.target.value)}
      />
      <TextField
        type='password'
        label='Password'
        color='secondary'
        required
        value={password}
        onChange={(v) => setPassword(v.target.value)}
      />
      <TextField
        label='Name'
        color='secondary'
        required
        value={name}
        onChange={(v) => setName(v.target.value)}
      />
      <TextField
        label='Position'
        color='secondary'
        required
        value={position}
        onChange={(v) => setPosition(v.target.value)}
      />
      <RoleHierarchySelector
        roleHierarchies={roleHierarchies}
        selectedRoleHierarchy={roleHierarchy}
        setSelectedRoleHierarchy={(roleHierarchy) =>
          setRoleHierarchy((prev) => ({ ...prev, ...roleHierarchy }))
        }
      />
      <UserRoleSelector
        selectedRole={roleHierarchy?.role}
        setSelectedRole={(role) => {
          setRoleHierarchy((prev) => ({ ...prev, role }))
        }}
      />

      <Button
        style={{ marginTop: '16px' }}
        variant='contained'
        color='primary'
        disabled={isSubmitDisabled}
        onClick={handleUpdateButtonClicked}
      >
        Update
      </Button>
      {!!resultMessage && (
        <Typography
          color={resultMessage!.type === 'error' ? 'error' : 'textPrimary'}
        >
          {resultMessage.message}
        </Typography>
      )}
    </div>
  )
}
